import { agentResearchTargetsQueryOptions } from "@/components/agents/query-options";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import type {
  ResearchTargetConnectionStatus,
  ResearchTargetIndexCompanyStatus,
  ResearchTargetIndexLeadStatus,
  ResearchTargetIndexRow,
  ResearchTargetResearchStatus,
} from "@/services/types/agent-gateway";
import { useQuery } from "@tanstack/react-query";
import {
  Building2,
  ChevronDown,
  ChevronRight,
  CircleDashed,
  Clock3,
  LoaderCircle,
  Search,
  Users,
} from "lucide-react";
import { type ReactNode, useState } from "react";

interface ResearchTargetsDashboardProps {
  agentKey: string;
  agentSelector?: ReactNode;
  tabs: ReactNode;
}

const companyStatusPresentation: Record<
  ResearchTargetIndexCompanyStatus,
  { label: string; className: string; dot: string }
> = {
  research_pending: {
    label: "Research pending",
    className: "border-amber-200 bg-amber-50 text-amber-700",
    dot: "bg-amber-500",
  },
  in_progress: {
    label: "In progress",
    className: "border-blue-200 bg-blue-50 text-blue-700",
    dot: "bg-blue-500",
  },
  needs_review: {
    label: "Needs review",
    className: "border-violet-200 bg-violet-50 text-violet-700",
    dot: "bg-violet-500",
  },
  researched: {
    label: "Researched",
    className: "border-emerald-200 bg-emerald-50 text-emerald-700",
    dot: "bg-emerald-500",
  },
  rejected: {
    label: "Rejected",
    className: "border-red-200 bg-red-50 text-red-700",
    dot: "bg-red-500",
  },
  skipped: {
    label: "Skipped",
    className: "border-slate-200 bg-slate-50 text-slate-600",
    dot: "bg-slate-400",
  },
};

const leadStatusPresentation: Record<
  ResearchTargetIndexLeadStatus,
  { label: string; className: string }
> = {
  research_pending: {
    label: "Research pending",
    className: "border-amber-200 bg-amber-50 text-amber-700",
  },
  in_progress: {
    label: "In progress",
    className: "border-blue-200 bg-blue-50 text-blue-700",
  },
  needs_review: {
    label: "Needs review",
    className: "border-violet-200 bg-violet-50 text-violet-700",
  },
  verified: {
    label: "Verified",
    className: "border-emerald-200 bg-emerald-50 text-emerald-700",
  },
  invite_sent: {
    label: "Invite sent",
    className: "border-cyan-200 bg-cyan-50 text-cyan-700",
  },
  rejected: {
    label: "Rejected",
    className: "border-red-200 bg-red-50 text-red-700",
  },
};

const connectionStatusPresentation: Record<
  ResearchTargetConnectionStatus,
  { label: string; className: string }
> = {
  checking: {
    label: "Verifying connection…",
    className: "border-slate-200 bg-slate-50 text-slate-600",
  },
  connection_check_failed: {
    label: "Connection check failed",
    className: "border-red-200 bg-red-50 text-red-700",
  },
  connection_request_needed: {
    label: "Connection request needed",
    className: "border-amber-200 bg-amber-50 text-amber-700",
  },
  pending_connection_approval: {
    label: "Pending connection approval",
    className: "border-violet-200 bg-violet-50 text-violet-700",
  },
  approved_to_connect: {
    label: "Approved to connect",
    className: "border-emerald-200 bg-emerald-50 text-emerald-700",
  },
  connection_request_sending: {
    label: "Sending connection request…",
    className: "border-blue-200 bg-blue-50 text-blue-700",
  },
  awaiting_acceptance: {
    label: "Connection sent · awaiting acceptance",
    className: "border-cyan-200 bg-cyan-50 text-cyan-700",
  },
  connected: {
    label: "Connected",
    className: "border-emerald-200 bg-emerald-50 text-emerald-700",
  },
  connection_request_rejected: {
    label: "Connection request rejected",
    className: "border-red-200 bg-red-50 text-red-700",
  },
  connection_request_failed: {
    label: "Connection request failed",
    className: "border-red-200 bg-red-50 text-red-700",
  },
  connection_delivery_unknown: {
    label: "Connection delivery needs review",
    className: "border-amber-200 bg-amber-50 text-amber-800",
  },
  connection_acceptance_expired: {
    label: "Connection acceptance expired",
    className: "border-slate-200 bg-slate-50 text-slate-600",
  },
};

const researchStatusPresentation: Record<
  ResearchTargetResearchStatus,
  { label: string; className: string }
> = {
  research_pending: {
    label: "Pending research",
    className: "border-slate-200 bg-slate-50 text-slate-600",
  },
  in_progress: {
    label: "Verifying…",
    className: "border-blue-200 bg-blue-50 text-blue-700",
  },
  needs_review: {
    label: "Pending review",
    className: "border-amber-200 bg-amber-50 text-amber-700",
  },
  verified: {
    label: "Qualified",
    className: "border-emerald-200 bg-emerald-50 text-emerald-700",
  },
  rejected: {
    label: "Not qualified",
    className: "border-red-200 bg-red-50 text-red-700",
  },
  discovered: {
    label: "Profile discovered",
    className: "border-slate-200 bg-slate-50 text-slate-600",
  },
  verifying_profile: {
    label: "Verifying profile…",
    className: "border-blue-200 bg-blue-50 text-blue-700",
  },
  company_verified: {
    label: "Company verified",
    className: "border-blue-200 bg-blue-50 text-blue-700",
  },
  company_ambiguous: {
    label: "Company association unclear",
    className: "border-amber-200 bg-amber-50 text-amber-700",
  },
  company_mismatch: {
    label: "Company mismatch",
    className: "border-red-200 bg-red-50 text-red-700",
  },
  persona_ineligible: {
    label: "Not eligible",
    className: "border-red-200 bg-red-50 text-red-700",
  },
  qualified: {
    label: "Qualified",
    className: "border-emerald-200 bg-emerald-50 text-emerald-700",
  },
  verification_failed: {
    label: "Profile verification failed",
    className: "border-red-200 bg-red-50 text-red-700",
  },
};

function StatusBadge({ status }: { status: ResearchTargetIndexCompanyStatus }) {
  const presentation = companyStatusPresentation[status];
  return (
    <Badge
      className={cn("gap-2 rounded-full", presentation.className)}
      variant="outline"
    >
      <span className={cn("size-1.5 rounded-full", presentation.dot)} />
      {presentation.label}
    </Badge>
  );
}

function LeadStatusBadge({
  status,
  approvalStatus,
  connectionStatus,
  researchStatus,
}: {
  status: ResearchTargetIndexLeadStatus;
  approvalStatus?: "pending_review" | "approved";
  connectionStatus?: ResearchTargetConnectionStatus | null;
  researchStatus?: ResearchTargetResearchStatus | null;
}) {
  const presentation = connectionStatus
    ? connectionStatusPresentation[connectionStatus]
    : approvalStatus === "approved"
      ? {
          label: "Approved",
          className: "border-emerald-200 bg-emerald-50 text-emerald-700",
        }
      : researchStatus
        ? researchStatusPresentation[researchStatus]
        : leadStatusPresentation[status];
  return (
    <Badge
      className={cn("rounded-full", presentation.className)}
      variant="outline"
    >
      {presentation.label}
    </Badge>
  );
}

function MetricCard({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Building2;
  label: string;
  value: number;
}) {
  return (
    <article className="rounded-xl border border-slate-200 bg-white p-4 shadow-xs md:p-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-slate-500 text-sm">{label}</p>
          <p className="mt-2 font-semibold text-2xl text-slate-900">{value}</p>
        </div>
        <span className="flex size-9 items-center justify-center rounded-full bg-brand-lightGreen text-brand-darkGreen">
          <Icon aria-hidden="true" className="size-4" />
        </span>
      </div>
    </article>
  );
}

function formatUpdatedAt(value: string | null) {
  if (!value) {
    return "—";
  }
  const timestamp = new Date(value).getTime();
  const seconds = Math.round((timestamp - Date.now()) / 1000);
  const formatter = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  if (Math.abs(seconds) < 60) {
    return formatter.format(seconds, "second");
  }
  const minutes = Math.round(seconds / 60);
  if (Math.abs(minutes) < 60) {
    return formatter.format(minutes, "minute");
  }
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) {
    return formatter.format(hours, "hour");
  }
  return formatter.format(Math.round(hours / 24), "day");
}

function getLinkedInProfileUrl(value: string) {
  try {
    const candidate = value.trim();
    if (candidate.startsWith("/") || candidate.startsWith("\\")) {
      return null;
    }
    const url = new URL(
      candidate.includes("://") ? candidate : `https://${candidate}`,
    );
    const hostname = url.hostname.toLowerCase().replace(/\.$/, "");
    if (
      url.protocol !== "https:" ||
      url.username ||
      url.password ||
      (hostname !== "linkedin.com" && !hostname.endsWith(".linkedin.com"))
    ) {
      return null;
    }
    return url.href;
  } catch {
    return null;
  }
}

function ContactList({
  row,
}: { row: ResearchTargetIndexRow["leads"][number] }) {
  if (row.contacts.length === 0) {
    return <span className="text-slate-400">—</span>;
  }
  return (
    <div className="flex min-w-0 flex-wrap gap-1.5">
      {row.contacts.map((contact) => {
        const className =
          "max-w-52 truncate rounded-md border border-slate-200 bg-white px-2 py-1 text-slate-600 text-xs";
        const linkedInUrl =
          contact.channel === "linkedin"
            ? getLinkedInProfileUrl(contact.value)
            : null;
        if (linkedInUrl) {
          return (
            <a
              className={`${className} hover:border-blue-300 hover:text-blue-700 hover:underline`}
              href={linkedInUrl}
              key={`${contact.channel}:${contact.value}`}
              rel="noopener noreferrer"
              target="_blank"
              title={contact.value}
            >
              LinkedIn
            </a>
          );
        }
        return (
          <span
            className={className}
            key={`${contact.channel}:${contact.value}`}
            title={contact.value}
          >
            {contact.channel === "linkedin" ? "LinkedIn" : contact.value}
          </span>
        );
      })}
    </div>
  );
}

function ResearchTargetsTable({ rows }: { rows: ResearchTargetIndexRow[] }) {
  const [expandedIds, setExpandedIds] = useState<Set<string> | null>(null);
  const defaults = new Set(rows.slice(0, 2).map((row) => row.id));
  const expanded = expandedIds ?? defaults;
  const toggle = (rowId: string) => {
    const next = new Set(expanded);
    if (next.has(rowId)) {
      next.delete(rowId);
    } else {
      next.add(rowId);
    }
    setExpandedIds(next);
  };

  return (
    <div className="hidden overflow-hidden rounded-xl border border-slate-200 bg-white lg:block">
      <Table aria-label="Research targets">
        <TableHeader className="bg-slate-50/80">
          <TableRow className="hover:bg-slate-50/80">
            <TableHead className="w-10">
              <span className="sr-only">Expand</span>
            </TableHead>
            <TableHead>Company</TableHead>
            <TableHead>Segment</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Individuals</TableHead>
            <TableHead>Updated</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <ResearchTargetTableGroup
              expanded={expanded.has(row.id)}
              key={row.id}
              onToggle={() => toggle(row.id)}
              row={row}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ResearchTargetTableGroup({
  expanded,
  onToggle,
  row,
}: {
  expanded: boolean;
  onToggle: () => void;
  row: ResearchTargetIndexRow;
}) {
  return (
    <>
      <TableRow className="bg-white">
        <TableCell>
          <Button
            aria-expanded={expanded}
            aria-label={`${expanded ? "Collapse" : "Expand"} ${row.name}`}
            onClick={onToggle}
            size="icon"
            type="button"
            variant="ghost"
          >
            {expanded ? <ChevronDown /> : <ChevronRight />}
          </Button>
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-3">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand-lightGreen text-brand-darkGreen">
              <Building2 className="size-4" />
            </span>
            <div className="min-w-0">
              <p className="font-medium text-slate-900">{row.name}</p>
              <p className="truncate text-slate-500 text-xs">
                {row.domain || row.session_title}
              </p>
            </div>
          </div>
        </TableCell>
        <TableCell className="text-slate-600">{row.segment || "—"}</TableCell>
        <TableCell>
          <StatusBadge status={row.status} />
        </TableCell>
        <TableCell>{row.leads.length}</TableCell>
        <TableCell className="text-slate-500">
          {formatUpdatedAt(row.updated_at)}
        </TableCell>
      </TableRow>
      {expanded &&
        row.leads.map((lead) => (
          <TableRow className="bg-slate-50/50" key={`${row.id}:${lead.id}`}>
            <TableCell />
            <TableCell className="pl-10">
              <p className="font-medium text-slate-800">{lead.name}</p>
              <p className="text-slate-500 text-xs">
                {lead.role_title || "Role not available"}
              </p>
            </TableCell>
            <TableCell>
              <ContactList row={lead} />
            </TableCell>
            <TableCell>
              <LeadStatusBadge
                approvalStatus={lead.approval_status}
                connectionStatus={lead.connection_status}
                researchStatus={lead.research_status}
                status={lead.status}
              />
            </TableCell>
            <TableCell className="text-slate-400">Individual</TableCell>
            <TableCell className="text-slate-500">
              {formatUpdatedAt(lead.updated_at)}
            </TableCell>
          </TableRow>
        ))}
    </>
  );
}

function ResearchTargetCards({ rows }: { rows: ResearchTargetIndexRow[] }) {
  return (
    <div
      className="grid gap-3 lg:hidden"
      data-testid="mobile-research-target-cards"
    >
      {rows.map((row) => (
        <article
          className="rounded-xl border border-slate-200 bg-white p-4 shadow-xs"
          key={row.id}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand-lightGreen text-brand-darkGreen">
                <Building2 className="size-4" />
              </span>
              <div className="min-w-0">
                <h3 className="font-medium text-slate-900 [overflow-wrap:anywhere]">
                  {row.name}
                </h3>
                <p className="truncate text-slate-500 text-xs">
                  {row.domain || row.segment || row.session_title}
                </p>
              </div>
            </div>
            <StatusBadge status={row.status} />
          </div>
          {row.leads.length > 0 && (
            <div className="mt-4 space-y-3 border-slate-100 border-t pt-3">
              {row.leads.map((lead) => (
                <div className="min-w-0" key={`${row.id}:${lead.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium text-slate-800 text-sm">
                        {lead.name}
                      </p>
                      <p className="text-slate-500 text-xs">
                        {lead.role_title || "Role not available"}
                      </p>
                    </div>
                    <LeadStatusBadge
                      approvalStatus={lead.approval_status}
                      connectionStatus={lead.connection_status}
                      researchStatus={lead.research_status}
                      status={lead.status}
                    />
                  </div>
                  <div className="mt-2">
                    <ContactList row={lead} />
                  </div>
                </div>
              ))}
            </div>
          )}
          <p className="mt-3 text-slate-400 text-xs">
            Updated {formatUpdatedAt(row.updated_at)}
          </p>
        </article>
      ))}
    </div>
  );
}

export function ResearchTargetsDashboard({
  agentKey,
  agentSelector,
  tabs,
}: ResearchTargetsDashboardProps) {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [page, setPage] = useState(1);
  const filters = { q: query, status, sessionId, page, pageSize: 25 };
  const targetsQuery = useQuery(
    agentResearchTargetsQueryOptions(agentKey, filters),
  );

  if (targetsQuery.isPending) {
    return <ResearchTargetsDashboardSkeleton tabs={tabs} />;
  }

  if (targetsQuery.isError) {
    return (
      <>
        {tabs}
        <div className="rounded-xl border border-red-200 bg-red-50 px-6 py-10 text-center">
          <p className="font-medium text-red-800">
            Research targets are unavailable
          </p>
          <p className="mt-1 text-red-700 text-sm">
            The operational data store could not be read. Please try again.
          </p>
          <Button
            className="mt-4"
            onClick={() => targetsQuery.refetch()}
            type="button"
            variant="outline"
          >
            Try again
          </Button>
        </div>
      </>
    );
  }

  const data = targetsQuery.data;
  return (
    <>
      <section
        aria-label="Research target summary"
        className="grid grid-cols-2 gap-3 lg:grid-cols-4"
      >
        <MetricCard
          icon={Building2}
          label="Companies"
          value={data.summary.companies}
        />
        <MetricCard
          icon={LoaderCircle}
          label="In progress"
          value={data.summary.in_progress}
        />
        <MetricCard
          icon={CircleDashed}
          label="Research pending"
          value={data.summary.research_pending}
        />
        <MetricCard
          icon={Users}
          label="Individuals found"
          value={data.summary.individuals_found}
        />
      </section>

      {tabs}

      <section aria-labelledby="research-targets-heading" className="space-y-4">
        <h2 className="sr-only" id="research-targets-heading">
          Research targets
        </h2>
        <fieldset className="flex min-w-0 flex-col gap-3 lg:flex-row">
          <legend className="sr-only">Research target filters</legend>
          <div className="relative flex-1">
            <Search
              aria-hidden="true"
              className="-translate-y-1/2 absolute top-1/2 left-3 size-4 text-slate-400"
            />
            <Input
              aria-label="Search research targets"
              className="h-10 bg-white pl-9"
              onChange={(event) => {
                setQuery(event.target.value);
                setPage(1);
              }}
              placeholder="Search companies and individuals"
              type="search"
              value={query}
            />
          </div>
          {agentSelector}
          <label className="sr-only" htmlFor="research-status-filter">
            Filter by status
          </label>
          <select
            className="hidden h-10 rounded-md border border-slate-200 bg-white px-3 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 lg:block"
            id="research-status-filter"
            onChange={(event) => {
              setStatus(event.target.value);
              setPage(1);
            }}
            value={status}
          >
            <option value="">All statuses</option>
            <option value="in_progress">In progress</option>
            <option value="needs_review">Needs review</option>
            <option value="research_pending">Research pending</option>
            <option value="researched">Researched</option>
            <option value="rejected">Rejected</option>
            <option value="skipped">Skipped</option>
          </select>
          <label className="sr-only" htmlFor="research-session-filter">
            Filter by session
          </label>
          <select
            className="hidden h-10 max-w-64 rounded-md border border-slate-200 bg-white px-3 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 lg:block"
            id="research-session-filter"
            onChange={(event) => {
              setSessionId(event.target.value);
              setPage(1);
            }}
            value={sessionId}
          >
            <option value="">All sessions</option>
            {data.sessions.map((session) => (
              <option key={session.id} value={session.id}>
                {session.title}
              </option>
            ))}
          </select>
        </fieldset>

        <p aria-live="polite" className="text-slate-500 text-sm">
          Showing {data.results.length} of {data.pagination.count} companies
        </p>

        {data.results.length === 0 ? (
          <div className="flex flex-col items-center rounded-xl border border-slate-200 border-dashed bg-white px-6 py-14 text-center">
            <Clock3 className="size-6 text-slate-400" />
            <h3 className="mt-3 font-semibold text-slate-900">
              No research targets found
            </h3>
            <p className="mt-1 text-slate-500 text-sm">
              Targets will appear as agent research sessions make progress.
            </p>
          </div>
        ) : (
          <>
            <ResearchTargetsTable rows={data.results} />
            <ResearchTargetCards rows={data.results} />
          </>
        )}

        {(page > 1 || data.pagination.next_page) && (
          <nav
            aria-label="Research target pages"
            className="flex items-center justify-end gap-2"
          >
            <Button
              disabled={page === 1}
              onClick={() => setPage((value) => Math.max(1, value - 1))}
              type="button"
              variant="outline"
            >
              Previous
            </Button>
            <span className="text-slate-500 text-sm">Page {page}</span>
            <Button
              disabled={!data.pagination.next_page}
              onClick={() => setPage((value) => value + 1)}
              type="button"
              variant="outline"
            >
              Next
            </Button>
          </nav>
        )}
      </section>
    </>
  );
}

function ResearchTargetsDashboardSkeleton({ tabs }: { tabs: ReactNode }) {
  return (
    <>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[0, 1, 2, 3].map((item) => (
          <Skeleton className="h-28 rounded-xl" key={item} />
        ))}
      </div>
      {tabs}
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-72 rounded-xl" />
    </>
  );
}
